import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  CheckCircleRounded,
  SearchRounded,
  SendRounded,
  LockRounded,
  PublicRounded,
} from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useDialog } from "../../DialogContext";
import { backend_api } from "../../../static/server";
import { appendTeamMemberLawyerId } from "../../../styles/styles";
import {
  buildPresetCommentEntity,
  getCommentFormModuleOptions,
  getCommentModuleDefinition,
} from "./commentActionUtils";
import { emitCommentNotificationsRefresh } from "./useCommentNotifications";

const MAX_COMMENT_LENGTH = 5000;

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    backgroundColor: "#FFFFFF",
  },
};

const useDebouncedValue = (value, delay = 220) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => window.clearTimeout(timeoutId);
  }, [delay, value]);

  return debouncedValue;
};

const buildCommentRequestUrl = (basePath, teamMemberLawyerId) =>
  appendTeamMemberLawyerId(`${backend_api}${basePath}`, teamMemberLawyerId);

const sortOptionsByLabel = (items = []) =>
  [...items].sort((left, right) =>
    String(left?.label || left?.moduleLabel || "").localeCompare(
      String(right?.label || right?.moduleLabel || ""),
    ),
  );

const VisibilityChip = ({ label, icon, selected, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      px: 1.25,
      py: 0.4,
      borderRadius: "999px",
      border: "1.5px solid",
      borderColor: selected ? "primary.main" : "#D0D5DD",
      bgcolor: selected ? "primary.main" : "#FFFFFF",
      color: selected ? "#fff" : "text.secondary",
      cursor: "pointer",
      userSelect: "none",
      transition: "all 0.15s ease",
      fontWeight: 600,
      fontSize: "0.72rem",
      "&:hover": {
        borderColor: "primary.main",
        color: selected ? "#fff" : "primary.main",
      },
    }}
  >
    {icon}
    {label}
  </Box>
);

const CreateCommentForm = ({
  entityOptions = [],
  onSuccess,
  variant = "dialog",
  module,
  object_id,
  object_name,
  defaultEntryType = "comment",
  lockEntryType = false,
}) => {
  const { closeDialog } = useDialog();
  const { token, teamMemberLawyerId } = useSelector((state) => state.user);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [serverError, setServerError] = useState("");
  const [moduleSearch, setModuleSearch] = useState("");
  const [recordSearch, setRecordSearch] = useState("");
  const isInline = variant === "inline";
  const hasPresetRecord = Boolean(module && object_id);

  const presetEntity = useMemo(
    () => buildPresetCommentEntity({ module, object_id, object_name }),
    [module, object_id, object_name],
  );
  const presetModuleDefinition = useMemo(
    () => getCommentModuleDefinition(module),
    [module],
  );

  const debouncedModuleSearch = useDebouncedValue(moduleSearch);
  const debouncedRecordSearch = useDebouncedValue(recordSearch);

  const moduleOptions = useMemo(() => {
    const seen = new Set();
    const derivedOptions = entityOptions.reduce((accumulator, option) => {
      if (!option?.moduleKey || seen.has(option.moduleKey)) return accumulator;
      seen.add(option.moduleKey);
      accumulator.push({
        moduleKey: option.moduleKey,
        moduleLabel: option.moduleLabel,
      });
      return accumulator;
    }, []);

    if (derivedOptions.length > 0) return sortOptionsByLabel(derivedOptions);

    return getCommentFormModuleOptions().map((item) => ({
      moduleKey: item.moduleKey,
      moduleLabel: item.moduleLabel,
    }));
  }, [entityOptions]);

  const defaultModuleKey = hasPresetRecord
    ? presetModuleDefinition.moduleKey
    : moduleOptions[0]?.moduleKey || "";

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      moduleKey: defaultModuleKey,
      entity: presetEntity,
      entryType: defaultEntryType,
      content: "",
    },
  });

  const selectedModuleKey = watch("moduleKey");
  const selectedEntity = watch("entity");
  const selectedEntryType = watch("entryType");
  const contentValue = watch("content", "");

  const filteredModuleOptions = useMemo(() => {
    const selectedModule = moduleOptions.find(
      (o) => o.moduleKey === selectedModuleKey,
    );
    const selectedLabel = selectedModule?.moduleLabel || "";

    if (!debouncedModuleSearch || debouncedModuleSearch === selectedLabel) {
      return moduleOptions;
    }
    const normalizedQuery = debouncedModuleSearch.trim().toLowerCase();
    return moduleOptions.filter((option) =>
      option.moduleLabel?.toLowerCase().includes(normalizedQuery),
    );
  }, [debouncedModuleSearch, moduleOptions, selectedModuleKey]);

  const entityChoices = useMemo(() => {
    if (hasPresetRecord && presetEntity) return [presetEntity];

    const selectedLabel = selectedEntity?.label || "";
    const scopedItems = entityOptions.filter(
      (option) => option.moduleKey === selectedModuleKey,
    );

    if (!debouncedRecordSearch || debouncedRecordSearch === selectedLabel) {
      return sortOptionsByLabel(scopedItems);
    }

    const normalizedQuery = debouncedRecordSearch.trim().toLowerCase();
    return sortOptionsByLabel(
      scopedItems.filter((option) => {
        const searchValue = [
          option.label,
          option.moduleLabel,
          option.entityType,
          option.id,
        ]
          .join(" ")
          .toLowerCase();
        return searchValue.includes(normalizedQuery);
      }),
    );
  }, [
    debouncedRecordSearch,
    entityOptions,
    hasPresetRecord,
    presetEntity,
    selectedModuleKey,
    selectedEntity,
  ]);

  useEffect(() => {
    if (lockEntryType) {
      setValue("entryType", defaultEntryType, { shouldDirty: false });
    }
  }, [defaultEntryType, lockEntryType, setValue]);

  useEffect(() => {
    if (hasPresetRecord) {
      setValue("moduleKey", presetModuleDefinition.moduleKey, {
        shouldDirty: false,
      });
      setValue("entity", presetEntity, { shouldDirty: false });
      return;
    }

    if (!selectedModuleKey && moduleOptions.length > 0) {
      setValue("moduleKey", moduleOptions[0].moduleKey, { shouldDirty: false });
      return;
    }

    if (!selectedModuleKey) {
      setValue("entity", null, { shouldDirty: false });
      return;
    }

    if (!selectedEntity || selectedEntity.moduleKey !== selectedModuleKey) {
      const fallbackEntity =
        sortOptionsByLabel(
          entityOptions.filter(
            (option) => option.moduleKey === selectedModuleKey,
          ),
        )[0] || null;
      setValue("entity", fallbackEntity, { shouldDirty: false });
      return;
    }

    const stillExists = entityOptions.some(
      (option) =>
        option.id === selectedEntity.id &&
        option.moduleKey === selectedEntity.moduleKey,
    );
    if (!stillExists) setValue("entity", null, { shouldDirty: false });
  }, [
    entityOptions,
    hasPresetRecord,
    moduleOptions,
    presetEntity,
    presetModuleDefinition.moduleKey,
    selectedEntity,
    selectedModuleKey,
    setValue,
  ]);

  const onSubmit = async (formData) => {
    setServerError("");
    setSubmitStatus(null);

    const payload = {
      content: String(formData.content || "").trim(),
      entry_type: formData.entryType || defaultEntryType || "comment",
      entity_type: formData.entity?.entityType || presetEntity?.entityType,
      entity_id: formData.entity?.id || presetEntity?.id,
      mentions: [],
    };

    if (!payload.content) {
      setServerError("Comment cannot be empty.");
      return;
    }

    if (!payload.entity_type || !payload.entity_id) {
      setServerError("Choose a record before posting the comment.");
      return;
    }

    try {
      const url = buildCommentRequestUrl("comments/", teamMemberLawyerId);
      const response = await axios.post(url, payload, {
        headers: { Authorization: `Token ${token}` },
      });

      setSubmitStatus("success");
      reset({
        moduleKey: hasPresetRecord
          ? presetModuleDefinition.moduleKey
          : formData.moduleKey,
        entity: hasPresetRecord ? presetEntity : formData.entity,
        entryType: lockEntryType
          ? defaultEntryType
          : formData.entryType || defaultEntryType || "comment",
        content: "",
      });
      onSuccess?.(response.data);
      if ((formData.entryType || defaultEntryType || "comment") === "comment") {
        emitCommentNotificationsRefresh();
      }

      if (!isInline) window.setTimeout(() => closeDialog?.(), 900);
    } catch (error) {
      setSubmitStatus("error");
      const responseMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.content?.[0] ||
        error?.response?.data?.entity_id?.[0] ||
        error?.response?.data?.entity_type?.[0] ||
        "Failed to post comment.";
      setServerError(String(responseMessage));
    }
  };

  if (!isInline && submitStatus === "success") {
    return (
      <Box
        sx={{
          minHeight: 240,
          display: "grid",
          placeItems: "center",
          p: 3,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.25,
          }}
        >
          <CheckCircleRounded sx={{ fontSize: 52, color: "#0F9D58" }} />
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {selectedEntryType === "note" ? "Note saved" : "Comment posted"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedEntryType === "note"
              ? "Your private note is now attached to this record."
              : "The team can now see this update in the communication board."}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: isInline ? 1.4 : 2,
          p: isInline ? { xs: 1.75, md: 2.1 } : { xs: 2, sm: 3 },
        }}
      >
        {serverError ? <Alert severity="error">{serverError}</Alert> : null}

        {/* Visibility chips — top of form */}
        <Controller
          name="entryType"
          control={control}
          render={({ field }) =>
            lockEntryType ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                  }}
                >
                  Visibility
                </Typography>
                <VisibilityChip
                  label={
                    field.value === "note" ? "Note (Private)" : "Comment"
                  }
                  icon={
                    field.value === "note" ? (
                      <LockRounded sx={{ fontSize: "0.75rem" }} />
                    ) : (
                      <PublicRounded sx={{ fontSize: "0.75rem" }} />
                    )
                  }
                  selected
                  onClick={() => {}}
                />
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                  }}
                >
                  Visibility
                </Typography>
                <VisibilityChip
                  label="Comment"
                  icon={<PublicRounded sx={{ fontSize: "0.75rem" }} />}
                  selected={field.value === "comment"}
                  onClick={() => field.onChange("comment")}
                />
                <VisibilityChip
                  label="Note (Private)"
                  icon={<LockRounded sx={{ fontSize: "0.75rem" }} />}
                  selected={field.value === "note"}
                  onClick={() => field.onChange("note")}
                />
              </Box>
            )
          }
        />

        {/* Module + Entity row */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.25,
            alignItems: "flex-start",
          }}
        >
          <Controller
            name="moduleKey"
            control={control}
            rules={{ required: "Choose a module." }}
            render={({ field }) => {
              const selectedModule =
                moduleOptions.find((o) => o.moduleKey === field.value) || null;

              return (
                <Autocomplete
                  size="small"
                  options={filteredModuleOptions}
                  value={selectedModule}
                  onChange={(_, newValue) => {
                    field.onChange(newValue?.moduleKey || "");
                    setRecordSearch("");
                  }}
                  onInputChange={(_, value, reason) => {
                    if (reason === "input") setModuleSearch(value);
                    if (reason === "clear") setModuleSearch("");
                  }}
                  getOptionLabel={(option) => option?.moduleLabel || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.moduleKey === value.moduleKey
                  }
                  noOptionsText="No matching module"
                  disabled={hasPresetRecord}
                  sx={{ width: { xs: "100%", sm: 180 }, flexShrink: 0 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Module"
                      placeholder="Search module"
                      error={Boolean(errors.moduleKey)}
                      helperText={errors.moduleKey?.message || " "}
                      sx={fieldSx}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <SearchRounded sx={{ fontSize: "1rem" }} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {option.moduleLabel}
                      </Typography>
                    </Box>
                  )}
                />
              );
            }}
          />

          <Controller
            name="entity"
            control={control}
            rules={{ required: "Choose a record." }}
            render={({ field }) => (
              <Autocomplete
                size="small"
                value={field.value}
                onChange={(_, newValue) => field.onChange(newValue)}
                onInputChange={(_, value, reason) => {
                  if (reason === "input") setRecordSearch(value);
                  if (reason === "clear") setRecordSearch("");
                }}
                options={entityChoices}
                filterOptions={(options) => options}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) =>
                  option.id === value.id && option.moduleKey === value.moduleKey
                }
                noOptionsText={
                  selectedModuleKey
                    ? "No matching records"
                    : "Choose a module first"
                }
                disabled={hasPresetRecord || !selectedModuleKey}
                sx={{ flex: 1, width: { xs: "100%", sm: "auto" } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Choose record"
                    placeholder="Search by title, name, invoice, or document"
                    error={Boolean(errors.entity)}
                    helperText={errors.entity?.message || " "}
                    sx={{ ...fieldSx, width: "100%" }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchRounded sx={{ fontSize: "1rem" }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 0,
                        gap: 0.2,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {option.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.moduleLabel} #{option.id}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            )}
          />
        </Box>

        {/* Comment textarea */}
        <Controller
          name="content"
          control={control}
          rules={{
            required: "Write a comment for the team.",
            maxLength: {
              value: MAX_COMMENT_LENGTH,
              message: `Comments can be up to ${MAX_COMMENT_LENGTH} characters.`,
            },
            validate: (value) =>
              String(value || "").trim().length > 0 ||
              "Comment cannot be blank.",
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Comment"
              placeholder="Write a clear update so teammates understand the context quickly."
              multiline
              minRows={isInline ? 3 : 4}
              maxRows={8}
              fullWidth
              error={Boolean(errors.content)}
              helperText={
                errors.content?.message ||
                `${MAX_COMMENT_LENGTH - (contentValue?.length ?? 0)} characters remaining`
              }
              sx={fieldSx}
              inputProps={{ maxLength: MAX_COMMENT_LENGTH }}
            />
          )}
        />
      </Box>

      {/* Action row */}
      <Box
        sx={{
          height: "fit-content",
          borderTop: "1px solid #ddd",
          position: "sticky",
          bottom: 0,
          backdropFilter: "blur(10px)",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          p: "0.5rem",
          zIndex: 1000,
          gap: "0.5rem",
          bgcolor: "rgba(255, 255, 255, 0.8)",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
          {!isInline && (
            <Button
              variant="outlined"
              size="small"
              disabled={isSubmitting}
              onClick={() => closeDialog?.()}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            size="small"
            disabled={isSubmitting || !(selectedEntity || presetEntity)}
            endIcon={
              isSubmitting ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SendRounded fontSize="small" />
              )
            }
          >
            {isSubmitting ? "Posting..." : "Post comment"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CreateCommentForm;
